// App.js
import React, { useReducer, useState, useRef, useContext, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ko } from 'date-fns/locale';
import { getStorage, ref as storageRef, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { getDatabase, ref as dbRef, push, set } from 'firebase/database';
import { initializeApp } from 'firebase/app';
import { motion, AnimatePresence, delay } from 'framer-motion';
import NotFound from './NotFound';
import * as THREE from 'three';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import Hls from 'hls.js';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

// Firebase 설정과 초기화를 imports 아래, styled-components 정의 전에 추가
const firebaseConfig = {
  apiKey: "AIzaSyDFLElJ5z-7XFBWpfDFm-s-U1zYAei3L3A",
  authDomain: "bespace-3cd78.firebaseapp.com",
  databaseURL: "https://bespace-3cd78-default-rtdb.firebaseio.com",
  projectId: "bespace-3cd78",
  storageBucket: "bespace-3cd78.appspot.com",
  messagingSenderId: "909824875492",
  appId: "1:909824875492:web:cb53e53751e6b08bcf45f1"
};

// Firebase 초기화
const app = initializeApp(firebaseConfig);
const storage = getStorage(app);
const database = getDatabase(app);

// 1. 기본 스타일 컴포넌트들
const GlobalStyle = createGlobalStyle`
  :root {
    --primary-color: #FF4B4B;
    --secondary-color: #ffffff;
    --text-color: #ffffff;
    --background-color: #FF4B4B;
  }

  html, body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    height: 100%;
    
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
    }
  }

  body {
    background: var(--primary-color);
    font-family: 'Noto Sans KR', sans-serif;
    color: var(--text-color);
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    letter-spacing: -0.03em;
    position: fixed;
    width: 100%;
    
    @supports (-webkit-touch-callout: none) {
      min-height: -webkit-fill-available;
      padding-bottom: env(safe-area-inset-bottom);
    }
  }

  #root {
    height: 100vh;
    
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
      padding-bottom: env(safe-area-inset-bottom);
    }
  }

  @media (max-width: 1024px) {
    html {
      font-size: 14px;
    }
  }

  @media (max-width: 768px) {
    html {
      font-size: 12px;
    }
  }

  // react-datepicker 달력 팝업 스타일 수정
  @media (max-width: 768px) {
    .react-datepicker {
      transform: scale(1.3);
      transform-origin: center center;
      position: fixed !important;
      top: 50% !important;
      left: 50% !important;
      transform: translate(-50%, -50%) scale(1.3) !important;
    }
    
    .react-datepicker-popper {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: none !important;
      margin: 0 !important;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(0, 0, 0, 0.5); // 배경 오버레이 추가
      z-index: 1000;
    }

    .react-datepicker__month-container {
      float: none;
      background: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }

  // GlobalStyle 내부에 다음 스타일을 추가
  .react-datepicker {
    font-family: 'Noto Sans KR', sans-serif;
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 16px;
    background: white;
  }

  .react-datepicker__header {
    background-color: white;
    border-bottom: none;
    padding: 0;
    margin-bottom: 16px;
  }

  .react-datepicker__current-month {
    color: #FF4B4B;
    font-weight: 500;
    font-size: 16px;
    margin-bottom: 16px;
  }

  .react-datepicker__day-name {
    color: #999;
    font-size: 13px;
    width: 36px;
    height: 36px;
    line-height: 36px;
    margin: 0;
  }

  .react-datepicker__day {
    width: 36px;
    height: 36px;
    line-height: 36px;
    margin: 0;
    color: #333;
    font-size: 14px;

    &:hover {
      background-color: rgba(255, 75, 75, 0.1);
      border-radius: 50%;
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #FF4B4B;
    color: white;
    border-radius: 50%;
    font-weight: normal;

    &:hover {
      background-color: #FF4B4B;
      border-radius: 50%;
    }
  }

  .react-datepicker__day--outside-month {
    color: #ccc;
  }

  .react-datepicker__navigation {
    top: 20px;
    
    &--previous {
      left: 20px;
    }
    
    &--next {
      right: 20px;
    }
  }

  .react-datepicker__navigation-icon {
    &::before {
      border-color: #FF4B4B;
      border-width: 2px 2px 0 0;
      height: 8px;
      width: 8px;
    }
  }

  .react-datepicker__day--disabled {
    color: #ccc;
    cursor: not-allowed;

    &:hover {
      background-color: transparent;
    }
  }

  // 모바일 환경 최적화
  @media (max-width: 768px) {
    .react-datepicker {
      transform: scale(1.2);
      transform-origin: top center;
    }

    .react-datepicker-popper {
      transform: none !important;
      inset: 0 !important;
      position: fixed !important;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      padding: 20px;
    }

    .react-datepicker__month-container {
      float: none;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
    }

    .react-datepicker__day {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 13px;
    }

    .react-datepicker__day-name {
      width: 32px;
      height: 32px;
      line-height: 32px;
      font-size: 12px;
    }
  }
`;

// 2. 확장된 스타일 컴포넌트들
const Button = styled.button`
  background-color: ${props => props.filled ? '#ffffff' : 'transparent'};
  color: ${props => props.filled ? '#FF4B4B' : '#ffffff'};
  border: 2px solid #ffffff;
  padding: 0.8rem 1.6rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0.5rem;
  min-width: 120px;
  border-radius: 30px;  // 둥근 리 추가

  &:hover {
    background-color: ${props => props.filled ? '#f8f8f8' : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const BackButton = styled(Button)`
  position: absolute;
  left: 40px;
  top: 40px;
  min-width: 48px;
  width: 48px;
  height: 48px;
  padding: 0;
  font-size: 0.9rem;
  background-color: white;
  color: #FF4B4B;
  border: 1px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '◀';
    font-size: 16px;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: transparent;
    color: white;
    
    &::before {
      color: white;
    }
  }

  @media (max-width: 768px) {
    left: 20px;
    top: 40px;
    width: 44px;
    height: 44px;
    min-width: 44px;
    z-index: 100;
  }
`;

// NextButton 스타일 ���포넌트 수정
const NextButton = styled(motion.button)`
  position: absolute;
  bottom: 40px;
  right: 40px;
  min-width: 130px;
  height: 52px;
  padding: 0 40px;
  font-size: 18px;
  background-color: white;
  color: #FF4B4B;
  border: 1px solid white;
  border-radius: 30px;
  display: flex;
  align-items: center;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  opacity: 1;
  transform: translateY(0);

  &::after {
    content: '▶';
    margin-left: 8px;
    font-size: 15px;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: transparent;
    color: white;
    
    &::after {
      color: white;
    }
  }

  @media (max-width: 768px) {
    display: none; // 모바일에서는 PC용 NextButton 숨김
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 290px !important;
  height: 48px;
  padding: 0 16px;
  border-radius: 4px;
  border: none;
  background: white;
  font-size: 15px;
  cursor: pointer;
  color: #FF4B4B;

  &::placeholder {
    color: #FF4B4B;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 58.5% !important;  // CommercialDatePicker와 동일한 너비로 설정
    height: 48px;
    border-radius: 6px;
  }
`;

const ImagePreviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 5px;
  margin-top: 10px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
`;

const ImagePreviewWrapper = styled.div`
  position: relative;
  width: 80%;
  padding-top: 80%; // 1:1 비율 유지
`;

const ImagePreview = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0; // 모서리를 직각으로 변경
`;

const DeleteImageButton = styled(Button)`
  position: absolute;
  top: 0.5px;
  right: 0.5px;
  padding: 0;
  width: 20px;
  height: 20px;
  min-width: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  border-radius: 0; // 모서리를 직각으로 변경
  background-color: rgba(0, 0, 0, 0.5);
  border: none;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const MemoizedImagePreview = React.memo(({ image, index, handleRemoveImage }) => (
  <ImagePreviewWrapper>
    <ImagePreview src={URL.createObjectURL(image)} alt={`업로드된 이미지 ${index + 1}`} />
    <DeleteImageButton onClick={(e) => handleRemoveImage(e, index)} type="button">
      <FaTimes size={12} />
    </DeleteImageButton>
  </ImagePreviewWrapper>
));

// PrivacyPolicyBox를 가장 먼저 정의
const PrivacyPolicyBox = styled.div`
  width: 100%;
  max-height: 150px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 0.8rem;
  margin-bottom: 1rem;
  color: #FF4B4B;
  font-size: 0.85rem;
  line-height: 1.4;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 75, 75, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 75, 75, 0.3);
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 75, 75, 0.4);
  }

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    max-height: 120px;
    font-size: 0.8rem;
  }

  @media (max-width: 375px) {
    width: 90%;
    max-height: 120px;
    padding: 12px;
    font-size: 13px;
    margin-bottom: 8px;
  }
`;

// CheckboxLabel 정의 전에 Checkbox 컴포넌트 추가
const Checkbox = styled.input`
  margin-right: 10px;
  cursor: pointer;
  width: 16px;
  height: 16px;

  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }
`;

// CheckboxLabel 의
const PrivacyCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  margin-top: 10px;
  width: 80%;
  text-align: left;
  color: var(--text-color);

  @media (max-width: 375px) {
    width: 90%;
    margin-top: 8px;
    font-size: 13px;
  }
`;

// 그 다음 PersonalPrivacyBox 정의
const PersonalPrivacyBox = styled(PrivacyPolicyBox)`
  @media (min-width: 769px) {
    max-height: 150px; // PC 화면에서 높이를 150px로 제한
  }

  @media (max-width: 768px) {
    width: 82%;
    max-height: 120px;
    padding: 12px;
    font-size: 13px;
    margin-bottom: 8px;
    border-radius: 6px;
  }
`;

// 기존 스타일 컴포넌트들과 함께 배치
const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 71, 77, 1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  @media (max-width: 768px) {
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
    }
  }
`;

// CanvasContainer 스타일 추가
const CanvasContainer = styled.div`
  width: 300px;
  height: 300px;
  position: relative;
`;

// LoadingText 스타일 수정
const LoadingText = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 300;
  margin-top: 2rem;
  letter-spacing: -0.03em;
`;

// LoadingAnimation 컴포넌트 추가
function LoadingAnimation() {
  const canvasRef = useRef(null);

  useEffect(() => {
    let animationFrameId;
    let cube, renderer, scene, camera;
    let rotateValue = 0.01;

    const init = () => {
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(75, 1, 1, 10000);
      camera.position.z = 500;
      
      // 큐브 크기 축소
      const geometry = new THREE.BoxGeometry(300, 300, 300, 10, 10, 10);
      const material = new THREE.MeshBasicMaterial({
        color: 0xffffff,
        wireframe: true
      });
      cube = new THREE.Mesh(geometry, material);
      scene.add(cube);
      
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setSize(300, 300);
      renderer.setClearColor(0xd1684e, 0);
      
      if (canvasRef.current) {
        canvasRef.current.appendChild(renderer.domElement);
      }
    };

    const animate = () => {
      cube.rotation.x += rotateValue;
      cube.rotation.y += rotateValue;
      
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };

    init();
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (canvasRef.current && renderer && renderer.domElement) {
        canvasRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <CanvasContainer ref={canvasRef} />;
}

// LoadingOverlay 컴포넌트 수정
const LoadingOverlay = ({ message }) => {
  return (
    <LoadingContainer>
      <LoadingAnimation />
      <LoadingText>{message}</LoadingText>
    </LoadingContainer>
  );
};

// 초기 상태
const initialState = {
  spaceType: '',
  address: '',
  roomCount: '',
  bathroomCount: '',
  areaSize: '',
  budget: '',
  residence: '',
  vacant: '',
  moveInDate: '',
  consultationDate: '',
  businessType: '',
  empty: '',
  openingDate: '',
  designImages: [], // Array of File objects
  privacyPolicyAccepted: false,
  name: '',
  phone: '',
  email: '',
  selectedPage: '',
  referralSource: '',
};

// 유효성 검사 함수

// Reducer 함수 수정
const reducer = (state, action) => {
  let newState;
  switch (action.type) {
    case 'SET_FIELD':
      newState = { ...state, [action.field]: action.value };
      break;
    case 'SET_IMAGES':
      newState = { ...state, designImages: action.value };
      break;
    case 'REMOVE_IMAGE':
      newState = {
        ...state,
        designImages: state.designImages.filter((_, index) => index !== action.index),
      };
      break;
    case 'RESET':
      newState = initialState;
      break;
    case 'SET_ALL':
      const { designImages } = state;
      newState = { ...state, ...action.value, designImages };
      break;
    default:
      newState = state;
      break;
  }
  
  // designImages를 제외하고 로컬 스토리지에 저장
  const stateToSave = { ...newState };
  delete stateToSave.designImages;
  localStorage.setItem('formState', JSON.stringify(stateToSave));
  
  return newState;
};

// 폼 유효성 검사
const validateForm = (state) => {
  const requiredFields = ['name', 'phone', 'email', 'privacyPolicyAccepted'];
  return requiredFields.every(field => state[field] && state[field].toString().trim() !== '');
};

// 페이지 애니메이션 variants 정의

// 필수 필드 누락 경고 함수
const missingFieldsAlert = (state, page) => {
  const requiredFields = {
    personal: [
      { field: 'name', label: '이름' },
      { field: 'phone', label: '연락처' },
      { field: 'email', label: '이메일' },
      { field: 'privacyPolicyAccepted', label: '개인정보 처리방침 동의' }
    ],
    commercial: [
      { field: 'businessType', label: '업종' },
      { field: 'address', label: '주소' },
      { field: 'areaSize', label: '평수' },
      { field: 'budget', label: '예산' },
      { field: 'empty', label: '공실 여부' },
      { field: 'openingDate', label: '오픈 예정일' },
      { field: 'consultationDate', label: '상담/실측 날짜' }
    ],
    residential: [
      { field: 'spaceType', label: '공간 유형' },  // spaceType을 필수 필드로 추가
      { field: 'address', label: '주소' },
      { field: 'roomCount', label: '방 개수' },
      { field: 'bathroomCount', label: '욕실 개수' },
      { field: 'areaSize', label: '평수' },
      { field: 'budget', label: '예산' },
      { field: 'residence', label: '거주 여부' },
      { field: 'moveInDate', label: '입주 예정일' },
      { field: 'consultationDate', label: '상담/실측 날짜' }
    ],
    residential1: [  // residential1 페이지의 필수 필드 추가
      { field: 'spaceType', label: '공간 유형' }
    ]
  };

  if (!requiredFields[page]) {
    console.error(`유효하지 않은 페이지 유형: ${page}`);
    return true;
  }

  const missingFields = requiredFields[page].filter(field => !state[field.field] || state[field.field].toString().trim() === '');

  if (missingFields.length > 0) {
    const missingLabels = missingFields.map(field => field.label).join(', ');
    alert(`다음 필수 항목을 입력해주세요: ${missingLabels}`);
    return true;
  }

  return false;
};

// 유틸리티 함수: 날짜를 UTC로 변환
const convertToUTCDate = (date) => {
  return new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ));
};

// 메인 앱 컴포넌트
function App({ state, dispatch }) {
  const location = useLocation();
  const { direction, isTransitioning } = useContext(TransitionContext);
  const { isVideoTransitionComplete } = useContext(VideoContext);

  useEffect(() => {
    // iOS Safari viewport 설정
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover';
    document.head.appendChild(viewportMeta);

    return () => {
      document.head.removeChild(viewportMeta);
    };
  }, []);

  return (
    <>
      <AnimatePresence mode="wait" initial={false} custom={direction}>
        {isVideoTransitionComplete && (
          <Routes location={location} key={location.pathname}>
            <Route 
              path="/" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 렌더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 추가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <IntroPage />
                </motion.div>
              } 
            />
            <Route 
              path="/select" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 렌더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 추가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <SelectPage dispatch={dispatch} />
                </motion.div>
              } 
            />
            <Route 
              path="/residential1" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 렌더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 추가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <Residential1 
                    dispatch={dispatch} 
                    state={state} 
                  />
                </motion.div>
              } 
            />
            {/* 나머지 라우트들  방식로 수정 */}
            <Route 
              path="/residential2" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 더링 최적
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <Residential2 
                    dispatch={dispatch} 
                    state={state} 
                  />
                </motion.div>
              } 
            />
            <Route 
              path="/commercial" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 렌더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 추가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <CommercialPage 
                    dispatch={dispatch} 
                    state={state} 
                  />
                </motion.div>
              } 
            />
            <Route 
              path="/personal-info1" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D ��더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 추가
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <PersonalInfo1 
                    dispatch={dispatch} 
                    state={state} 
                  />
                </motion.div>
              } 
            />
            <Route 
              path="/personal-info2" 
              element={
                <motion.div
                  custom={direction}
                  variants={pageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    willChange: 'transform, opacity',
                    backfaceVisibility: 'hidden', // 3D 렌더링 최적화
                    WebkitBackfaceVisibility: 'hidden',
                    perspective: '1000px', // 3D 공간감 �����
                    WebkitPerspective: '1000px',
                    transformStyle: 'preserve-3d',
                    WebkitTransformStyle: 'preserve-3d'
                  }}
                >
                  <PersonalInfo2 
                    dispatch={dispatch} 
                    state={state} 
                  />
                </motion.div>
              } 
            />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </AnimatePresence>
    </>
  );
}

// VideoBackground 컴포넌트 수정
const VideoBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -2; // 기존 -1에서 -2로 변경하여 인트로 페이지 컨테이너 아래로 배치
  overflow: hidden;

  @media (max-width: 768px) {
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
    }
  }
`;

// VideoWrapper 스타일 컴포넌트 수정
const VideoWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 177.77777778vh; /* 100 * 16 / 9 */
  height: 56.25vw; /* 100 * 9 / 16 */
  min-width: 100%;
  min-height: 100%;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    will-change: transform;
    opacity: 0; // 초기 불투명도 설정
    transition: opacity 0.5s ease-in-out; // 전환 효과 추가

    @media (max-width: 768px) {
      opacity: 0; // 모바일 환경에서 섬네일 숨김
    }
  }
`;

// VideoContext 수정
const VideoContext = React.createContext();

// 비디오 타임라인 정의
const VIDEO_TIMELINE = [
  { start: 0, end: 3.60 },
  { start: 3.60, end: 7.34 },
  { start: 7.34, end: 10.80 },
  { start: 10.80, end: 14.40 },
  { start: 14.40, end: 18.21 },
  { start: 18.21, end: 21.72 },
  { start: 21.72, end: 25.29 },
  { start: 25.29, end: 28.60 }
];

// VideoProvider 컴포넌트 수정
const VideoProvider = ({ children }) => {
  const playerRef = useRef(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [isVideoTransitionComplete, setIsVideoTransitionComplete] = useState(true);
  const location = useLocation();
  const prevLocationRef = useRef(location.pathname);

  useEffect(() => {
    // Vimeo Player API 스크립트 로드
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (playerRef.current) {
        const player = new window.Vimeo.Player(playerRef.current);
        
        player.ready().then(() => {
          setIsVideoReady(true);
          // 기본 설정
          player.setVolume(0);
          player.setLoop(true);
          player.setPlaybackRate(1.6);  // 재생 속도를 1.5배로 설정
          player.pause(); // 인트로 페이지에서 미리 로드만 하고 재생하지 않음
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (!isVideoReady || !playerRef.current) return;

    const player = new window.Vimeo.Player(playerRef.current);

    player.on('loaded', () => {
      // 비디오가 로드되면 불투명도를 1로 설정
      playerRef.current.style.opacity = 1;
    });

    if (location.pathname === '/success' || location.pathname === '/') {
      setIsVideoTransitionComplete(true);
      player.pause();
    } else {
      setIsVideoTransitionComplete(false);
      
      const nextSegment = prevLocationRef.current === '/' ? 
        0 : 
        currentSegment >= VIDEO_TIMELINE.length - 1 ? 0 : currentSegment + 1;
      
      setCurrentSegment(nextSegment);
      
      const segment = VIDEO_TIMELINE[nextSegment];
      player.setCurrentTime(segment.start).then(() => {
        player.play().catch(error => console.error('비디오 재생 실패:', error));
      });
      
      const checkTime = () => {
        player.getCurrentTime().then(currentTime => {
          if (currentTime >= segment.end) {
            player.pause();
            setIsVideoTransitionComplete(true);
          } else {
            requestAnimationFrame(checkTime);
          }
        });
      };
      requestAnimationFrame(checkTime);
    }
    
    prevLocationRef.current = location.pathname;
  }, [location.pathname, isVideoReady]);

  useEffect(() => {
    if (isVideoReady && playerRef.current && location.pathname !== '/' && location.pathname !== '/success') {
      const player = new window.Vimeo.Player(playerRef.current);
      player.play().catch(error => console.error('비디오 재생 실패:', error));
    }
  }, [isVideoReady, location.pathname]);

  return (
    <VideoContext.Provider value={{ 
      playerRef, 
      isVideoReady,
      isVideoPaused,
      currentSegment,
      isVideoTransitionComplete,
      setIsVideoPaused,
      setIsVideoReady 
    }}>
      {location.pathname !== '/success' && (
        <VideoBackground>
          <VideoWrapper>
            <iframe 
              ref={playerRef}
              src="https://player.vimeo.com/video/1035123847?badge=0&autopause=0&player_id=0&app_id=58479&background=1&controls=0&title=0&byline=0&portrait=0&transparent=1&autoplay=1&muted=1&loop=1&autopause=0&quality=1080p&thumbnail=0"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                opacity: 0, // 초기 불투명도 설정
                transition: 'opacity 0.5s ease-in-out', // 전환 효과 추가
              }}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              title="Background Video"
            />
          </VideoWrapper>
        </VideoBackground>
      )}
      {children}
    </VideoContext.Provider>
  );
};

// PageContainer 수정
const PageContainer = styled(motion.div)`
  width: 100vw;
  height: calc(100vh - 30vh);
  position: relative;
  background-color: rgba(255, 75, 75, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-sizing: border-box;
  margin: 15vh 0;

  @media (max-width: 768px) {
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
      min-height: -webkit-fill-available;
      padding-bottom: env(safe-area-inset-bottom);
    }
  }
`;

// 그 다음 SelectPageContainer 정의
const SelectPageContainer = styled(PageContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    padding: 0 20px; // 32px에서 20px로 수정
    margin: 0;
    height: 100vh;
    justify-content: flex-start;
    overflow-x: hidden; // 좌우 스크롤 방지
    position: fixed; // 고정 위치
    width: 100%; // 전체 너비
    left: 0; // 왼��� 정렬
    top: 0; // 상단 정렬
  }
`;

// SelectPageHeading 수�����
const SelectPageHeading = styled.h1`
  color: white;
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 300;
  position: absolute;
  left: 10vw;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  line-height: 1.2;
  white-space: pre-line;
  letter-spacing: -0.03em; // 자간 추가

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 140px; // 180px에서 140px로 ���정
    font-size: 36px; // 56px에서 36px로 수정
    text-align: left;
    line-height: 1.4;
    letter-spacing: -0.03em;
    width: 100%;
    padding: 0 10px; // 패�� 추가
  }

  @media (max-width: 375px) {
    margin-top: 120px;
    font-size: 32px;
  }
`;

// CardsContainer 수정
const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2vh;
  width: clamp(280px, 30vw, 400px);
  position: absolute;
  right: 10vw;
  top: 50%;
  transform: translateY(-50%);

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 140px; // 190px에서 140px로 수정
    top: auto;
    transform: none;
    width: auto;
    padding: 0 20px; // 32px에서 20px로 수정
    gap: 12px; // 16px에서 12px로 수정
  }

  @media (max-width: 375px) {
    bottom: 120px;
    gap: 10px;
  }
`;

// SelectCard 수정
const SelectCard = styled(motion.div)`
  background-color: ${props => props.isHovered ? '#FF4B4B' : 'white'};
  border: 1px solid white;
  border-radius: 7px;
  padding: clamp(1.5rem, 2vw, 2rem);
  cursor: pointer;
  width: 100%;
  height: clamp(60px, 8vh, 80px);
  display: flex;
  align-items: flex-end;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: transparent;
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    height: 80px; // 90px에서 80px로 수정
    padding: 1.25rem; // 1.5rem에서 1.25rem으로 수정
    width: 90%; // 90%에서 100%로 수정
    align-items: flex-end;
  }

  @media (max-width: 375px) {
    height: 70px;
    padding: 1rem;
  }
`;

const CardContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  height: 40px; // 이콘 높이와 동���게 설정
`;

const CardTextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
`;

// CardTitle ��정
const CardTitle = styled.h2`
  color: ${props => props.isHovered ? 'white' : '#FF4B4B'};
  font-size: clamp(1.2rem, 1.8vw, 1.5rem);
  font-weight: 500;
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 16px; // 18px에서 16px로 ����정
  }

  @media (max-width: 375px) {
    font-size: 15px;
  }
`;

// CardSubtitle ���정
const CardSubtitle = styled.p`
  color: ${props => props.isHovered ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.6)'};
  font-size: clamp(0.8rem, 1vw, 1rem);
  margin: 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 13px; // 14px에서 13px로 수정
  }

  @media (max-width: 375px) {
    font-size: 12px;
  }
`;

const CardIcon = styled.div`
  width: clamp(30px, 4vw, 50px);
  height: clamp(30px, 4vw, 50px);
  background-image: url(${props => props.icon});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: ${props => props.isHovered ? 'brightness(0) invert(1)' : 'none'};
`;

// SelectPage 컴포트 수정
function SelectPage({ dispatch }) {
  const navigate = useNavigate();
  const { playVideo, isVideoReady } = useContext(VideoContext);
  const { handleTransition } = useContext(TransitionContext);
  const [hoveredCard, setHoveredCard] = useState(null);

  const handleCardClick = (pageType) => {
    // 선택한 페이지 타입을 저��
    dispatch({ 
      type: 'SET_FIELD', 
      field: 'selectedPage', 
      value: pageType 
    });
    
    // 해당 페이지로 이동
    if (pageType === 'residential') {
      handleTransition('/residential1');
    } else if (pageType === 'commercial') {
      handleTransition('/commercial');
    }
  };

  return (
    <SelectPageContainer>

      <SelectPageHeading>어떤 공간을 원하시나요?</SelectPageHeading>
      
      <CardsContainer>
        <SelectCard
          isHovered={hoveredCard === 'residential'}
          onMouseEnter={() => setHoveredCard('residential')}
          onMouseLeave={() => setHoveredCard(null)}
          onClick={() => handleCardClick('residential')}
          whileHover={{ scale: 1.02 }}
        >
          <CardContent>
            <CardTextContent>
              <CardTitle isHovered={hoveredCard === 'residential'}>주거공간</CardTitle>
              <CardSubtitle isHovered={hoveredCard === 'residential'}>Residential interior</CardSubtitle>
            </CardTextContent>
            <CardIcon 
              icon="/icons/home.png"
              isHovered={hoveredCard === 'residential'}
            />
          </CardContent>
        </SelectCard>

        <SelectCard
          isHovered={hoveredCard === 'commercial'}
          onMouseEnter={() => setHoveredCard('commercial')}
          onMouseLeave={() => setHoveredCard(null)}
          onClick={() => handleCardClick('commercial')}
          whileHover={{ scale: 1.02 }}
        >
          <CardContent>
            <CardTextContent>
              <CardTitle isHovered={hoveredCard === 'commercial'}>상업공간</CardTitle>
              <CardSubtitle isHovered={hoveredCard === 'commercial'}>Commercial interior</CardSubtitle>
            </CardTextContent>
            <CardIcon 
              icon="/icons/store.png"
              isHovered={hoveredCard === 'commercial'}
            />
          </CardContent>
        </SelectCard>
      </CardsContainer>
    </SelectPageContainer>
  );
}

// CommercialPageContainer 수정
const CommercialPageContainer = styled(PageContainer)`
  @media (max-width: 768px) {
    padding: 0 20px;
    margin: 0;
    height: 100vh;
    justify-content: flex-start;
    overflow-y: auto;
    overflow-x: hidden;
    position: fixed;
    width: 100%;
    left: 0;
    top: 0;
    padding-bottom: env(safe-area-inset-bottom); // iOS safe area 추가
    
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
      min-height: -webkit-fill-available;
    }
  }
`;

// CommercialTitle 수정
const CommercialTitle = styled.h1`
  position: absolute;
  left: 280px;
  top: 45%;
  transform: translateY(-50%);
  font-size: 40px;
  font-weight: 300;
  color: white;
  line-height: 1.5;
  white-space: pre-line;
  width: 30%;
  letter-spacing: -0.03em;

  @media (max-width: 1024px) {
    font-size: 32px;
    left: 140px;
  }
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 140px; // 모든 페이지 동일하게 적용
    margin-bottom: 24px;
    padding: 0 20px; // 좌우 패딩 통일
    font-size: 32px;
    text-align: left;
    line-height: 1.4;
    width: 100%;
  }
`;

// CommercialFormContainer 수정
const CommercialFormContainer = styled.div`
  display: flex;
  gap: 20px;
  position: absolute;
  right: 200px;
  width: 700px;
  top: 50%;
  transform: translateY(-50%);
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    margin: 0;
    padding: 0 20px; // 좌우 패딩 통일
    padding-bottom: 0; // padding-bottom 제거
  }
`;

// FormGroup을 먼저 선언
const FormGroup = styled.div`
  margin-bottom: 0;
  width: 100%;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    width: 100%;
  }
`;

// FormLabel도 FormColumn 전에 선언
const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: white;
  font-size: 14px; // PC 환경에서 14px로 제한

  @media (max-width: 768px) {
    font-size: 14px; // 모바일에서는 기존 크기 유지
    margin-bottom: 4px;
  }

  @media (max-width: 375px) {
    font-size: 13px;
    margin-bottom: 4px;
  }
`;

// 그 다음 FormColumn 선언
const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  width: 50%;
  height: auto;
  padding: 10px 0;

  & > ${FormGroup} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;

    & > ${FormLabel} {
      margin-bottom: 12px;
    
  }
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 0 20px;
    gap: 16px;

    & > ${FormGroup} {
      height: auto;
      margin-bottom: 16px;
    }

    & > ${FormGroup} > ${FormLabel} {
      margin-bottom: 0.3rem;
    }
  }
  }
`;

// FormInput 수정
const FormInput = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid white;
  border-radius: 8px;
  background: white;
  color: #FF4B4B;
  font-size: 1rem;
  height: ${props => props.as === 'select' ? '52px' : 'auto'};
  max-width: ${props => props.as === 'select' ? '322px' : '290px'};

  &::placeholder {
    color: #FF4B4B;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 80%; // PersonalInfo1과 동일한 너비로 변경
    max-width: 80%; // PersonalInfo1과 동일한 너비로 변경
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 6px;
  }
`;

// CommercialInputWithUnit 수정
const CommercialInputWithUnit = styled.div`
  position: relative;
  width: 290px;
  height: 48px;

  @media (max-width: 768px) {
    width: 80%; // PersonalInfo1과 동일한 너비로 변경
    
    input {
      width: 100%;
      padding-right: 48px;
    }
  }
`;


const CommercialUnitText = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #FF4B4B;
  font-size: 15px;
  pointer-events: none;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: ${props => props.large ? '1rem' : '8px'};
  width: 100%;
  flex-wrap: wrap;
  
  & + & {
    margin-top: 20px;

    @media (max-width: 768px) {
    width: 80%;
    display: flex;
    gap: 12px;
    
    & + & {
      margin-top: 12px;
    }
  }
`;

// RadioButton 컴포넌트 수정
const RadioButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.selected ? 'transparent' : '#ffffff'};
  color: ${props => props.selected ? '#ffffff' : '#FF4B4B'};
  border: 2px solid white;
  padding: 0.8rem 1.6rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
  min-width: ${props => props.large ? '158px' : '158px'};
  border-radius: ${props => props.rounded ? '30px' : '10px'};
  height: ${props => props.height || '52px'};

  &:hover {
    background-color: ${props => props.selected ? 'rgba(255, 255, 255, 0.1)' : '#f8f8f8'};
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    height: ${props => props.isResidential1 || props.isPersonalInfo2 ? '44px' : '48px'};
    padding: 0;
    font-size: ${props => props.isResidential1 || props.isPersonalInfo2 ? '15px' : '14px'};
    border-radius: ${props => props.isResidential1 || props.isPersonalInfo2 ? '30px' : '6px'};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
  }

  @media (max-width: 375px) {
    height: ${props => props.isResidential1 || props.isPersonalInfo2 ? '40px' : '44px'};
    font-size: ${props => props.isResidential1 || props.isPersonalInfo2 ? '14px' : '13px'};
  }
`;

// FormSelect 컴포넌트 추가
const FormSelect = styled.select`
  width: 100%;
  padding: 0 1rem;
  border: 1px solid white;
  border-radius: 8px;
  background: white;
  color: #FF4B4B;
  font-size: 1rem;
  height: 52px;
  max-width: 322px;
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23FF4B4B' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  &::placeholder {
    color: #FF4B4B;
  }

  @media (max-width: 768px) {
    width: 90%;
    max-width: 90%;
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 6px;
    background-position: right 16px center;
  }
`;

// CommercialPage 컴포넌트 수정
function CommercialPage({ dispatch, state }) {
  const { handleTransition } = useContext(TransitionContext);

  const handleNext = () => {
    // 필수 필��� 검증
    if (missingFieldsAlert(state, 'commercial')) {
      return;
    }
    handleTransition('/personal-info1');
  };

  // 이미지 업로드 핸들러 추가
  const handleMultipleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentImages = Array.isArray(state.designImages) ? state.designImages : [];
    dispatch({ 
      type: 'SET_FIELD', 
      field: 'designImages', 
      value: [...currentImages, ...files] 
    });
  };

  // 이미지 삭제 핸들러 추가
  const handleRemoveImage = (e, index) => {
    e.preventDefault();
    const newImages = [...state.designImages];
    newImages.splice(index, 1);
    dispatch({ 
      type: 'SET_FIELD', 
      field: 'designImages', 
      value: newImages 
    });
  };

  const isFormValid = () => {
    const requiredFields = [
      'businessType',
      'address',
      'areaSize',
      'budget',
      'empty',
      'openingDate',
      'consultationDate'
    ];
    return requiredFields.every(field => state[field] && state[field].toString().trim() !== '');
  };

  return (
    <CommercialPageContainer>
      <BackButton onClick={() => handleTransition('/select', true)} />

      <CommercialTitle>
        정확한 상담을 위해{'\n'}몇 가지 물을게요!
      </CommercialTitle>

      <CommercialFormContainer>
        {/* 왼�� 컬럼 */}
        <FormColumn>
          {/* 새로 추가되는 FormGroup */}
          <FormGroup>
            <FormLabel>파트너사 선정시 중요하게 생각하는것은 무엇인가요?</FormLabel>
            <PrioritySelector
              value={state.priority || ''}
              onChange={(value) => dispatch({
                type: 'SET_FIELD',
                field: 'priority',
                value: value
              })}
            />
          </FormGroup>

          {/* 기존 FormGroup */}
          <FormGroup>
            <FormLabel>개업하실 업종을 입력해주세요.</FormLabel>
            <FormInput 
              placeholder="업종을 입력하세요"
              value={state.businessType || ''}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'businessType',
                value: e.target.value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>해당 공간의 주소를 입력해주세요.</FormLabel>
            <FormInput 
              placeholder="상세한 주소를 입력하세요"
              value={state.address || ''}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'address',
                value: e.target.value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>해당 공간의 평수는 어떻게 되나요?</FormLabel>
            <FormSelect
              value={state.areaSize || ''}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'areaSize',
                value: e.target.value
              })}
            >
              <option value="">선택하세요</option>
              <option value="10평대 이하">10평대 이하</option>
              <option value="10평대">10평대</option>
              <option value="20평대">20평대</option>
              <option value="30평대">30평대</option>
              <option value="40평대">40평대</option>
              <option value="50평대">50평대</option>
              <option value="60평대 이상">60평대 이상</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>염두해두신 예산은 어느 정도인가요?</FormLabel>
            <BudgetInputWrapper>
              <BudgetInput
                type="number"
                placeholder="예산을 입력하세요"
                value={state.budget || ''}
                onChange={(e) => dispatch({
                  type: 'SET_FIELD',
                  field: 'budget',
                  value: e.target.value
                })}
              />
              <BudgetUnitText>만원</BudgetUnitText>
            </BudgetInputWrapper>
          </FormGroup>
        </FormColumn>

        {/* 오른쪽 럼 */}
        <FormColumn>
          <FormGroup>
            <FormLabel>현재 해당 공간은 공실인가요?</FormLabel>
            <YesNoRadioGroup>
              <YesNoButton 
                selected={state.empty === '네'}
                onClick={() => dispatch({
                  type: 'SET_FIELD',
                  field: 'empty',
                  value: '네'
                })}
              >
                네
              </YesNoButton>
              <YesNoButton 
                selected={state.empty === '아니요'}
                onClick={() => dispatch({
                  type: 'SET_FIELD',
                  field: 'empty',
                  value: '아니요'
                })}
              >
                아니요
              </YesNoButton>
            </YesNoRadioGroup>
          </FormGroup>

          <FormGroup>
            <FormLabel>오픈 예정일은 언제인가요?</FormLabel>
            <CommercialDatePickerWrapper>
              <CommercialDatePicker
                selected={state.openingDate ? new Date(state.openingDate) : null}
                onChange={(date) => dispatch({
                  type: 'SET_FIELD',
                  field: 'openingDate',
                  value: date ? date.toISOString().split('T')[0] : ''
                })}
                placeholderText="날짜를 선택하세요"
                dateFormat="yyyy-MM-dd"
                locale={ko}
                minDate={new Date()} // 추가: 오늘 이전 날짜 선택 불가
              />
            </CommercialDatePickerWrapper>
          </FormGroup>

          <FormGroup>
            <FormLabel>상담/실측은 언제 방문할까요?</FormLabel>
            <CommercialDatePickerWrapper>
              <CommercialDatePicker
                selected={state.consultationDate ? new Date(state.consultationDate) : null}
                onChange={(date) => dispatch({
                  type: 'SET_FIELD',
                  field: 'consultationDate',
                  value: date ? date.toISOString().split('T')[0] : ''
                })}
                placeholderText="날짜를 선택하세요"
                dateFormat="yyyy-MM-dd"
                locale={ko}
                minDate={new Date()} // 추가: 오늘 이전 날짜 선택 불가
              />
            </CommercialDatePickerWrapper>
          </FormGroup>

          <FormGroup>
            <FormLabel>참고자료를 첨부해주세요. (이미지 파일)</FormLabel>
            <FileUploadButton>
              파일 선택
              <input
                type="file"
                accept="image/*"
                onChange={handleMultipleImageUpload}
                multiple
                style={{ display: 'none' }}
              />
            </FileUploadButton>
            <ImagePreviewContainer>
              {Array.isArray(state.designImages) && state.designImages.map((image, index) => (
                <MemoizedImagePreview
                  key={index}
                  image={image}
                  index={index}
                  handleRemoveImage={handleRemoveImage}
                />
              ))}
            </ImagePreviewContainer>
          </FormGroup>
        </FormColumn>
      </CommercialFormContainer>

      {/* PC용 NextButton */}
      <NextButton onClick={handleNext}>
        NEXT
      </NextButton>

      {/* 모바일용 NextButton */}
      <MobileNextButtonWrapper>
        <NextButton onClick={handleNext}>
          NEXT
        </NextButton>
      </MobileNextButtonWrapper>
    </CommercialPageContainer>
  );
}

// 새로운 스일 컴포넌트 추가
const SplitFormContainer = styled.div`
  display: flex;
  gap: 20px;
  position: absolute;
  right: 200px;
  width: 700px;
  top: 50%;
  transform: translateY(-50%);
  
  @media (max-width: 1440px) {
    right: 90px;
    width: 660px;
  }
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin-top: 20px;
    padding-bottom: 120px;
  }

  @media (max-width: 375px) {
    padding: 0;
    margin-top: 16px;
  }
`;

// LeftColumn 수정
const LeftColumn = styled(FormColumn)`
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 20px;
    gap: 16px;

    ${FormGroup} {
      margin-bottom: 0;
    }
  }
`;

// RightColumn 수정
const RightColumn = styled(FormColumn)`
  @media (max-width: 768px) {
    width: 100%;
    padding: 0 20px;
    gap: 16px;

    ${FormGroup} {
      margin-bottom: 0;
    }

    ${PrivacyPolicyBox} {
      width: 90%;
    }
  }
`;

// PersonalInfo2 컴포넌트
function PersonalInfo2({ dispatch, state }) {
  const { handleTransition } = useContext(TransitionContext);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // Add this line
  const startTime = useRef(Date.now());

  const handleNext = async () => {
    if (!validateForm(state)) {
      alert('모든 필수 항목을 올바르게 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      let imageUrls = [];
      if (state.designImages && state.designImages.length > 0) {
        const totalImages = state.designImages.length;
        
        const uploadPromises = state.designImages.map((file, index) => {
          const uniqueFileName = `images/${Date.now()}_${file.name}`;
          const imageRef = storageRef(storage, uniqueFileName);
          const uploadTask = uploadBytesResumable(imageRef, file);
          
          return new Promise((resolve, reject) => {
            uploadTask.on('state_changed',
              (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes);
                const totalProgress = (index + progress) / totalImages;
                setUploadProgress(totalProgress * 100);
              },
              reject,
              async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                resolve(downloadURL);
              }
            );
          });
        });

        imageUrls = await Promise.all(uploadPromises);
      }

      // 한국 시간으로 submittedAt 설정
      const koreaTime = new Date().toLocaleString('en-US', { 
        timeZone: 'Asia/Seoul',
        hour12: false 
      });

      // 데이터 필드명을 한글로 변경
      const finalFormData = {
        '선택공간': state.selectedPage === 'residential' ? '주거공간' : '상업공간',  // 추가
        '공간유형': state.spaceType,
        '주소': state.address,
        '방개수': state.roomCount,
        '욕실개수': state.bathroomCount,
        '평수': state.areaSize,
        '예산': state.budget,
        '거주여부': state.residence,
        '입주예정일': state.moveInDate,
        '상담실측날짜': state.consultationDate,
        '업종': state.businessType,
        '공실여부': state.empty,
        '오픈예정일': state.openingDate,
        '참고이미지': imageUrls,
        '이름': state.name,
        '연락처': state.phone,
        '이메일': state.email,
        '문의내용': state.inquiry,
        '개인정보동의': state.privacyPolicyAccepted,
        '유입경로': state.referralSource,
        '중요도': state.priority,
        '제출시간': koreaTime,
        '상태': 'submitted'
      };

      const formDataRef = dbRef(database, '홈페이지');
      const newFormSubmissionRef = push(formDataRef);
      await set(newFormSubmissionRef, finalFormData);

      dispatch({ type: 'RESET' });
      localStorage.removeItem('formState');

      const elapsedTime = Date.now() - startTime.current;
      const remainingTime = Math.max(3000 - elapsedTime, 0);
      
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      alert('제출이 완료되었습니다.');
      navigate('/success');
    } catch (error) {
      console.error('데이터 저장 중 오류 발생:', error);
      alert('제출 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const isFormValid = () => {
    return state.referralSource && state.referralSource.trim() !== '';
  };

  return (
    <>
      {isSubmitting && <LoadingOverlay message="전송중입니다..." />}
      <ResidentialPageContainer>
        <BackButton onClick={() => handleTransition('/personal-info1', true)} />

        <ResidentialTitle>
          혹시,{'\n'}어떻게 들어오셨나요?
        </ResidentialTitle>

        <Residential1FormContainer>
          <Residential1FormColumn>
            <Residential1FormGroup>
              <PersonalInfo2RadioGroup>
                {['인터넷 검색', '지인 추천', '인스타그램'].map((source) => (
                  <RadioButton
                    key={source}
                    selected={state.referralSource === source}
                    onClick={() => dispatch({
                      type: 'SET_FIELD',
                      field: 'referralSource',
                      value: source
                    })}
                    isPersonalInfo2={true}
                  >
                    {source}
                  </RadioButton>
                ))}
              </PersonalInfo2RadioGroup>
              <PersonalInfo2RadioGroup>
                {['네이버 광고', '네이버 카페', '기타'].map((source) => (
                  <RadioButton
                    key={source}
                    selected={state.referralSource === source}
                    onClick={() => dispatch({
                      type: 'SET_FIELD',
                      field: 'referralSource',
                      value: source
                    })}
                    isPersonalInfo2={true}
                  >
                    {source}
                  </RadioButton>
                ))}
              </PersonalInfo2RadioGroup>
            </Residential1FormGroup>
          </Residential1FormColumn>
        </Residential1FormContainer>

        {/* PC용 NextButton */}
        <NextButton onClick={handleNext}>
          NEXT
        </NextButton>

        {/* 모바일용 NextButton */}
        <MobileNextButtonWrapper>
          <NextButton onClick={handleNext}>
            NEXT
          </NextButton>
        </MobileNextButtonWrapper>
      </ResidentialPageContainer>
    </>
  );
}

// 추가 스타�� 컴포넌트

// ���공 페이지 컴포넌트 추가
const SuccessPage = () => {
  const navigate = useNavigate();
  const { handleTransition } = useContext(TransitionContext);

  // handleExplore 함수 수정
  const handleExplore = (e) => {
    e.preventDefault(); // 기본 동작 방지
    
    // iOS 디바이스 체크
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
    
    // 새 창에서 열기
    if (isIOS && isKakao) {
      // 카카오톡 브라우저에서는 외부 브라우저로 강제 이동
      window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent('http://www.bespace.kr')}`;
    } else {
      // 일반적인 경우 새 창에서 열기
      window.open('http://www.bespace.kr', '_blank', 'noopener,noreferrer');
    }
  };

  const handleCall = () => {
    window.location.href = 'tel:01058417093';
  };

  // 컴포넌트 마운트/언마운트 시 cleanup 추가
  useEffect(() => {
    // 페이지 진입 시 스크롤 위치 초기화
    window.scrollTo(0, 0);
    
    // iOS에서 뒤로가기 시 스크립트 에러 방지
    const handlePopState = (e) => {
      e.preventDefault();
      // 필요한 경우 추가 cleanup 로직
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <IntroContainer>
      <LogoWrapper>
        <LogoImage src="bespace-logo.png" alt="Bespace Logo" />
      </LogoWrapper>
      
      <IntroMessageWrapper>
        <IntroMessage>
          {`소중한 답변\n감사합니다`}
        </IntroMessage>
        <IntroSubMessage>
          영업일 기준 2 ~ 3일 내로 연락드릴게요 :)
        </IntroSubMessage>
      </IntroMessageWrapper>

      <PlayButtonWrapper>
        <ExploreButton 
          onClick={handleExplore}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
        >
          bespace 둘러보기
        </ExploreButton>
        
        <DirectCallButton 
          onClick={handleCall}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.95 }}
        >
          다이렉트 전화상담
        </DirectCallButton>
      </PlayButtonWrapper>
    </IntroContainer>
  );
};

// PDF 뷰어 관련 스타일 컴포넌트 추가
const PDFViewerContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 1000;
  display: flex;
  flex-direction: column;

  .rpv-core__viewer {
    flex: 1;
    overflow: auto;
    width: 100%;
    height: 100%;
    margin: 0 !important;
  }

  // PDF 뷰어 UI 요소 숨기기
  .rpv-core__inner-pages {
    padding: 0 !important;
    margin: 0 !important;
  }

  // 모든 UI 요소 숨기기
  .rpv-core__toolbar {
    display: none !important;
  }

  .rpv-core__menu-bar {
    display: none !important;
  }

  .rpv-core__minimal-button {
    display: none !important;
  }

  .rpv-core__navigation {
    display: none !important;
  }

  .rpv-core__zoom-popover {
    display: none !important;
  }

  .rpv-core__default-layout__toolbar {
    display: none !important;
  }

  .rpv-core__default-layout__sidebar {
    display: none !important;
  }

  .rpv-core__default-layout__main {
    padding: 0 !important;
    margin: 0 !important;
  }

  .rpv-core__default-layout__container {
    padding: 0 !important;
    margin: 0 !important;
  }

  // 모바일 환경 최적화
  @media (max-width: 768px) {
    // ... 기존 모바일 스타일 유지 ...
  }
`;

const PDFCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #FF4B4B;
  color: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: background-color 0.3s;

  &:hover {
    background: #ff3333;
  }

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    font-size: 18px;
  }
`;

// 페이지 전환 관련 컨텍트 추가
const TransitionContext = React.createContext();

// TransitionProvider 컴포넌트 수정
const TransitionProvider = ({ children }) => {
  const [direction, setDirection] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { videoRef, isVideoReady } = useContext(VideoContext);
  const transitionTimeoutRef = useRef(null);
  const previousLocationRef = useRef(location.pathname);

  // 브라저 뒤로가기/앞으로가기 감
  useEffect(() => {
    const handlePopState = () => {
      const isBack = previousLocationRef.current.split('/').length > location.pathname.split('/').length;
      setDirection(isBack ? -1 : 1);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location]);

  // 현재 경로 저장
  useEffect(() => {
    previousLocationRef.current = location.pathname;
  }, [location]);

  const handleTransition = async (path, isBack = false) => {
    if (!isVideoReady || isTransitioning) return;

    setIsTransitioning(true);
    setDirection(isBack ? -1 : 1);

    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    // RAF를 사용하여 더 부드러운 애메이션
    requestAnimationFrame(() => {
      transitionTimeoutRef.current = setTimeout(() => {
        navigate(path);
        
        // 이중 RAF로 렌더링 최적화
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsTransitioning(false);
          });
        });
      }, 400); // 애니메이션 duration과 맞춤
    });
  };

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <TransitionContext.Provider value={{ 
      direction,
      isTransitioning,
      handleTransition 
    }}>
      {children}
    </TransitionContext.Provider>
  );
};

// 페이지 애니메이션 variants 수정
const pageVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      type: "tween", // tween에서 tween으로 변경
      stiffness: 300, // 스프링 강성 추가
      damping: 30,    // 감�� 추가
      mass: 1,        // 질량 추가
      duration: 0.4   // 지속시간 약간 가
    },
    willChange: "transform, opacity" // 성능 최적화
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      type: "tween",
      stiffness: 300,
      damping: 30,
      mass: 1,
      duration: 0.4,
      when: "beforeChildren" // 자식 요소보다 먼저 애니메이션 실행
    },
    willChange: "transform, opacity"
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      type: "tween",
      stiffness: 300,
      damping: 30,
      mass: 1,
      duration: 0.4
    },
    willChange: "transform, opacity"
  })
};
// MainApp 컴포넌트 수정
export default function MainApp() {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    // iOS Safari viewport 설정
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover';
    document.head.appendChild(viewportMeta);

    // iOS body 스타일 설정
    document.body.style.webkitTouchCallout = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.webkitTapHighlightColor = 'transparent';

    return () => {
      document.head.removeChild(viewportMeta);
    };
  }, []);

  return (
    <Router>
      <GlobalStyle />
      <VideoProvider>
        <TransitionProvider>
          <App state={state} dispatch={dispatch} />
        </TransitionProvider>
      </VideoProvider>
    </Router>
  );
}

const IntroContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(255, 75, 75, 1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-x: hidden; // 로 스크롤 방지
  -webkit-overflow-scrolling: touch; // iOS 스크롤 부드럽게

  @media (max-width: 768px) {
    padding: 0;
    position: fixed;
    height: 100vh;
    overflow-y: auto;
    overflow-x: hidden; // 가로 스크롤 방지 재확인
    justify-content: flex-start;
    width: 100%;
    left: 0;
    top: 0;
  }
`;

const LogoWrapper = styled.div`
  position: absolute;
  top: 100px;
  left: 100px;

  @media (max-width: 1024px) {
    top: 60px;
    left: 60px;
  }

  @media (max-width: 768px) {
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
  }
`;

const LogoImage = styled.img`
  width: 150px;
  height: auto;

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const IntroMessageWrapper = styled.div`
  position: absolute;
  left: 100px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 180px;
    width: 100%;
    padding: 0 32px;
    align-items: flex-start;
  }
`;

const IntroMessage = styled.div`
  font-size: 4.5rem;
  font-weight: 300;
  color: #ffffff;
  line-height: 1.4;
  white-space: pre-line;
  letter-spacing: -0.03em; // -0.02em에서 -0.03em으로 수정
  font-family: 'Noto Sans KR', sans-serif;

  @media (max-width: 1024px) {
    font-size: 3.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 40px; // 50px에서 40px로 수정
    text-align: left;
    line-height: 1.5;
    letter-spacing: -0.03em;
    font-weight: 300;
    padding-left: 15px; // 좌측 여 추가
  }

  @media (max-width: 375px) {
    font-size: 36px; // 모일 환경에서 더 작은 폰트 사이즈
  }
`;

// Add this styled component definition after IntroMessage and before PlayButtonWrapper
const IntroSubMessage = styled.div`
  font-size: 1.2rem;
  font-weight: 300;
  color: #ffffff;
  margin-top: 1rem;
  opacity: 0.8;
  letter-spacing: -0.03em;
  font-family: 'Noto Sans KR', sans-serif;

  @media (max-width: 1024px) {
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    text-align: left;
    line-height: 1.5;
    letter-spacing: -0.03em;
    padding-left: 15px;
    margin-top: 12px;
  }

  @media (max-width: 375px) {
    font-size: 15px;
  }
`;

// PlayButtonWrapper 스타일 컴포넌트 수정
const PlayButtonWrapper = styled.div`
  position: absolute;
  right: 100px;
  top: 62%;
  transform: translateY(-50%);

  @media (max-width: 1024px) {
    right: 60px;
  }

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 40px;
    top: 70%;
    transform: none;
    display: flex;
    flex-direction: column; // 세로 방향으로 정렬
    align-items: center; // 가운데 정렬
    justify-content: center;
    padding: 0 40px;
    gap: 16px; // 버튼 ���이 간격
  }
`;

// 인트로 페이지의 견적내기 버튼 스타일
const IntroPlayButton = styled(motion.button)`
  background-color: white;
  border: 1px solid white;
  color: #FF4B4B;
  padding: 15px 40px;
  font-size: 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  font-weight: 500;
  text-decoration: none;

  &::after {
    content: '▶';
    margin-left: 8px;
    font-size: 15px;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: transparent;
    color: white;
    
    &::after {
      color: white;
    }
  }

  @media (max-width: 768px) {
    width: 140px;
    height: 48px;
    justify-content: center;
    font-size: 16px;
    padding: 0;
  }
`;

// 성공 페이지의 Portfolio 버튼 스타일
const PortfolioButton = styled(motion.button)`
  background-color: white;
  border: 1px solid white;
  color: #FF4B4B;
  padding: 15px 40px;
  font-size: 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  font-weight: 500;
  text-decoration: none;
  min-width: 160px; // Portfolio 텍스트를 위한 최소 너비 추가

  &::after {
    content: '▶';
    margin-left: 8px;
    font-size: 15px;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: transparent;
    color: white;
    
    &::after {
      color: white;
    }
  }

  @media (max-width: 768px) {
    width: 160px; // 모바일에서도 일관된 너비 유지
    height: 48px;
    justify-content: center;
    font-size: 16px;
    padding: 0;
  }
`;

// WebsiteLink 컴포넌트 수정 전에 Safari 강제 실행 함수 추가
const openInSafari = (url) => {
  // iOS 디바이스 체크
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
  
  if (isIOS && isKakao) {
    // location.href를 사용하여 Safari로 강제 이동
    window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(url)}`;
  } else {
    // iOS가 아니거나 카카오톡이 아닌 경우 일반적인 방식으로 열기
    window.open(url, '_blank');
  }
};

// WebsiteLink 컴포넌트 수정
const WebsiteLink = styled.a`
  position: absolute;
  left: 100px;
  bottom: 100px;
  color: #ffffff;
  text-decoration: none;
  font-size: 1rem;
  opacity: 0.8;
  cursor: pointer;
  
  &:hover {
    opacity: 1;
  }

  @media (max-width: 1024px) {
    left: 20px;
    bottom: 60px;
  }

  @media (max-width: 768px) {
    display: absolute; 
    font-size: 1.5rem;
  }
`;

function IntroPage() {
  const { handleTransition } = useContext(TransitionContext);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  
  useEffect(() => {
    // 페이지 로드 3초 후 버튼 활성화
    const timer = setTimeout(() => {
      setIsButtonEnabled(true);
    }, 2300);

    return () => clearTimeout(timer);
  }, []);
  
  const handlePlayClick = () => {
    if (isButtonEnabled) {
      handleTransition('/select');
    }
  };

  const handleWebsiteLinkClick = (e) => {
    e.preventDefault();
    openInSafari('http://www.bespace.kr');
  };

  useEffect(() => {
    // 카카오톡 브라우저 체크 및 리다이렉트
    const isKakao = /KAKAOTALK/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    if (isIOS && isKakao) {
      const currentUrl = window.location.href;
      window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`;
    }
  }, []);

  return (
    <IntroContainer className="intro-container">
      <LogoWrapper>
        <LogoImage src="bespace-logo.png" alt="Bespace Logo" />
      </LogoWrapper>
      
      <IntroMessageWrapper>
        <IntroMessage>
          {`비스페이스의\n공간에 오신것을\n환영합니다`}
        </IntroMessage>
      </IntroMessageWrapper>
      
      <PlayButtonWrapper>
        <IntroPlayButton 
          onClick={handlePlayClick}
          whileHover={isButtonEnabled ? { scale: 1.02 } : {}}
          whileTap={isButtonEnabled ? { scale: 0.95 } : {}}
          disabled={!isButtonEnabled}
          style={{
            opacity: isButtonEnabled ? 1 : 0.5,
            cursor: isButtonEnabled ? 'pointer' : 'default',
            transition: 'opacity 0.3s ease'
          }}
        >
          견적내기
        </IntroPlayButton>
      </PlayButtonWrapper>

      <WebsiteLink 
        onClick={handleWebsiteLinkClick}
        href="http://www.bespace.kr"
      >
        www.bespace.kr
      </WebsiteLink>
    </IntroContainer>
  );
}

// CommercialDatePickerWrapper 수정
const CommercialDatePickerWrapper = styled.div`
  height: 52px;
  
  .react-datepicker-wrapper,
  .react-datepicker__input-container {
    height: 100%;
  }

  @media (max-width: 768px) {
    width: 80%;  // PersonalInfo1과 동일한 너비로 변경
    
    .react-datepicker-wrapper,
    .react-datepicker__input-container {
      width: 100%;
    }
  }
`;

// CommercialDatePicker 수정
const CommercialDatePicker = styled(DatePicker)`
  width: 290px;
  height: 52px;
  padding: 0 16px;
  border-radius: 10px;
  border: none;
  background: white;
  font-size: 15px;
  cursor: pointer;
  color: #FF4B4B;

  &::placeholder {
    color: #FF4B4B;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 100% !important;  // PersonalInfo1과 동일한 너비로 경
    height: 48px;
    border-radius: 6px;
  }
`;

// ResidentialFormContainer를 먼저 정의
const ResidentialFormContainer = styled(CommercialFormContainer)`
  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    padding-bottom: 0; // padding-bottom 제거
  }
`;

// 그 다음 Residential1FormContainer 의
const Residential1FormContainer = styled(ResidentialFormContainer)`
  position: absolute;
  right: 200px;
  width: 700px;
  top: 50%;
  transform: translateY(-50%);

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    padding: 0;
    margin-top: 40px;
    padding-bottom: 0; // padding-bottom 제거
  }
`;

// ResidentialPageContainer 수정 (personal-info1, personal-info2가 사용)
const ResidentialPageContainer = styled(PageContainer)`
  @media (max-width: 768px) {
    padding: 0 20px;
    margin: 0;
    height: 100vh;
    justify-content: flex-start;
    overflow-y: auto;
    overflow-x: hidden; // 가로 스크롤 방지
    position: fixed; // 고정 위치
    width: 100%; // 전체 너비
    left: 0; // 왼쪽 정��
    top: 0; // 상단 정렬
    padding-bottom: env(safe-area-inset-bottom); // iOS safe area 추가
    
    @supports (-webkit-touch-callout: none) {
      height: -webkit-fill-available;
      min-height: -webkit-fill-available;
    }
  }

  @media (max-width: 375px) {
    padding: 0 20px;
  }
`;

// ResidentialTitle 수정
const ResidentialTitle = styled.h1`
  position: absolute;
  left: 280px;
  top: 45%;
  transform: translateY(-50%);
  font-size: 40px;
  font-weight: 300;
  color: white;
  line-height: 1.5;
  white-space: pre-line;
  width: 30%;
  letter-spacing: -0.03em; // 자��� 추가

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 140px;
    padding: 0 32px;
    font-size: 36px;
    text-align: left;
    line-height: 1.4;
    width: 100%;
  }

  @media (max-width: 375px) {
    position: static;
    transform: none;
    margin-top: 120px;
    margin-bottom: 24px;
    padding: 0 20px;
    font-size: 32px;
    text-align: left;
    line-height: 1.4;
    width: 100%;
  }
`;

const Residential1FormColumn = styled(FormColumn)`
  width: 100%;
  height: auto;
  padding: 0;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0;
    gap: 16px;
  }
`;

const Residential1FormGroup = styled(FormGroup)`
  height: auto;
  gap: 20px;
  
  @media (max-width: 768px) {
    position: relative;
    padding-bottom: 0; // padding-bottom 제거
  }
`;

// ResidentialRadioGroup 스타일 컴포넌트 수정
const ResidentialRadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  transform: translateX(200px);
  
  & + & {
    margin-top: 20px;
  }

  @media (max-width: 768px) {
    transform: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
    padding: 0px;
    margin-top: 0;
    align-items: center; // ���가
    justify-items: center; // 추가
    
    & + & {
      margin-top: 12px;
    }

    // RadioButton 컨테이너 스타일 조정
    > button {
      width: 100%; // 버튼 너비를 그리드 셀에 맞춤
      justify-content: center; // 버튼 내��� 콘텐츠 중앙 정렬
    }
  }

  @media (max-width: 375px) {
    transform: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    width: 100%;
    padding: 0px;
    margin-top: 0;
    
    & + & {
      margin-top: 8px;
    }
  }
`;

function Residential1({ dispatch, state }) {
  const { handleTransition } = useContext(TransitionContext);

  const handleNext = () => {
    if (missingFieldsAlert(state, 'residential1')) {
      return;
    }
    handleTransition('/residential2');
  };

  const isFormValid = () => {
    return state.spaceType && state.spaceType.trim() !== '';
  };

  return (
    <ResidentialPageContainer>
      <BackButton onClick={() => handleTransition('/select', true)}>
      </BackButton>

      <ResidentialTitle>
        정확한 상담을 위해{'\n'}공간 유형을 선택해주세요!
      </ResidentialTitle>

      <Residential1FormContainer>
        <Residential1FormColumn>
          <Residential1FormGroup>
            <ResidentialRadioGroup>
              {['아파트', '단독주택', '빌라'].map((type) => (
                <RadioButton
                  key={type}
                  selected={state.spaceType === type}
                  onClick={() => dispatch({
                    type: 'SET_FIELD',
                    field: 'spaceType',
                    value: type
                  })}
                  isResidential1={true}
                >
                  {type}
                </RadioButton>
              ))}
            </ResidentialRadioGroup>
            <ResidentialRadioGroup>
              {['주상복합', '오피스텔', '기타'].map((type) => (
                <RadioButton
                  key={type}
                  selected={state.spaceType === type}
                  onClick={() => dispatch({
                    type: 'SET_FIELD',
                    field: 'spaceType',
                    value: type
                  })}
                  isResidential1={true}
                >
                  {type}
                </RadioButton>
              ))}
            </ResidentialRadioGroup>
          </Residential1FormGroup>
        </Residential1FormColumn>
      </Residential1FormContainer>

      {/* PC용 NextButton */}
      <NextButton onClick={handleNext}>
        NEXT
      </NextButton>

      {/* 모바일용 NextButton */}
      <MobileNextButtonWrapper>
        <NextButton onClick={handleNext}>
          NEXT
        </NextButton>
      </MobileNextButtonWrapper>
    </ResidentialPageContainer>
  );
}

function Residential2({ dispatch, state }) {
  const { handleTransition } = useContext(TransitionContext);

  const handleNextClick = () => {
    if (missingFieldsAlert(state, 'residential')) {
      return;
    }
    handleTransition('/personal-info1');
  };

  const handleMultipleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const currentImages = Array.isArray(state.designImages) ? state.designImages : [];
    dispatch({ 
      type: 'SET_FIELD', 
      field: 'designImages', 
      value: [...currentImages, ...files] 
    });
  };

  const handleRemoveImage = (e, index) => {
    e.preventDefault();
    const newImages = [...state.designImages];
    newImages.splice(index, 1);
    dispatch({ 
      type: 'SET_FIELD', 
      field: 'designImages', 
      value: newImages 
    });
  };

  return (
    <ResidentialPageContainer>
      <BackButton onClick={() => handleTransition('/residential1', true)}>
      </BackButton>

      <ResidentialTitle>
        정확한 상담을 위해{'\n'}몇 가지 물을게요!
      </ResidentialTitle>

      <ResidentialFormContainer>
        <FormColumn>
          <FormGroup>
            <FormLabel>파트너사 선정시 중요하게 생각하는것은 무엇인가요?</FormLabel>
            <PrioritySelector
              value={state.priority || ''}
              onChange={(value) => dispatch({
                type: 'SET_FIELD',
                field: 'priority',
                value: value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>해당 공간의 주소를 입력해주세요</FormLabel>
            <FormInput
              type="text"
              placeholder="상세한 주소"
              value={state.address}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'address',
                value: e.target.value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>방은 몇 개인가요?</FormLabel>
            <FormInput
              type="number"
              placeholder="숫자로 입력해 주세요"
              value={state.roomCount}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'roomCount',
                value: e.target.value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>평수는 어떻게 되나요?</FormLabel>
            <FormSelect
              value={state.areaSize || ''}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'areaSize',
                value: e.target.value
              })}
            >
              <option value="">선택하세요</option>
              <option value="10평대 이하">10평대 이하</option>
              <option value="10평대">10평대</option>
              <option value="20평대">20평대</option>
              <option value="30평대">30평대</option>
              <option value="40평대">40평대</option>
              <option value="50평대">50평대</option>
              <option value="60평대 이상">60평대 이상</option>
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>생각하시는 시공 예산은 어느 정도인가요?</FormLabel>
            <BudgetInputWrapper>
              <BudgetInput
                type="number"
                placeholder="예산 입력"
                value={state.budget || ''}
                onChange={(e) => dispatch({
                  type: 'SET_FIELD',
                  field: 'budget',
                  value: e.target.value
                })}
              />
              <BudgetUnitText>만원</BudgetUnitText>
            </BudgetInputWrapper>
          </FormGroup>
        </FormColumn>

        <FormColumn>
          <FormGroup>
            <FormLabel>현재 해당 공간에 거주 중 인가요?</FormLabel>
            <YesNoRadioGroup>
              <YesNoButton 
                selected={state.residence === '네'}
                onClick={() => dispatch({
                  type: 'SET_FIELD',
                  field: 'residence',
                  value: '네'
                })}
              >
                네
              </YesNoButton>
              <YesNoButton 
                selected={state.residence === '아니요'}
                onClick={() => dispatch({
                  type: 'SET_FIELD',
                  field: 'residence',
                  value: '아니요'
                })}
              >
                아니요
              </YesNoButton>
            </YesNoRadioGroup>
          </FormGroup>

          <FormGroup>
            <FormLabel>욕실은 몇 개인가요?</FormLabel>
            <FormInput
              type="number"
              placeholder="숫자로 입력해 주세요"
              value={state.bathroomCount}
              onChange={(e) => dispatch({
                type: 'SET_FIELD',
                field: 'bathroomCount',
                value: e.target.value
              })}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>입주 예정일이 언제인가요?</FormLabel>
            <FormInput
              as={StyledDatePicker}
              selected={state.moveInDate ? new Date(state.moveInDate) : null}
              onChange={(date) => dispatch({
                type: 'SET_FIELD',
                field: 'moveInDate',
                value: date ? convertToUTCDate(date).toISOString().split('T')[0] : '',
              })}
              dateFormat="yyyy-MM-dd"
              placeholderText="날짜를 선택하세요"
              locale={ko}
              minDate={new Date()} // 추가: 오늘 이전 날짜 선택 불가
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>상담/실측은 언제 방문하기를 원하시나요?</FormLabel>
            <FormInput
              as={StyledDatePicker}
              selected={state.consultationDate ? new Date(state.consultationDate) : null}
              onChange={(date) => dispatch({
                type: 'SET_FIELD',
                field: 'consultationDate',
                value: date ? convertToUTCDate(date).toISOString().split('T')[0] : '',
              })}
              dateFormat="yyyy-MM-dd"
              placeholderText="날짜를 선택하세요"
              locale={ko}
              minDate={new Date()} // 추가: 오늘 이전 날짜 선택 불가
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>참고자료를 첨부해주세요. (이미지 파일)</FormLabel>
            <FileUploadButton>
              파일 선택
              <input
                type="file"
                accept="image/*"
                onChange={handleMultipleImageUpload}
                multiple
                style={{ display: 'none' }}
              />
            </FileUploadButton>
            <ImagePreviewContainer>
              {Array.isArray(state.designImages) && state.designImages.map((image, index) => (
                <MemoizedImagePreview
                  key={index}
                  image={image}
                  index={index}
                  handleRemoveImage={handleRemoveImage}
                />
              ))}
            </ImagePreviewContainer>
          </FormGroup>
        </FormColumn>
      </ResidentialFormContainer>

      {/* PC용 NextButton */}
      <NextButton 
        onClick={handleNextClick}
        style={{
          opacity: 1,
          '@media (max-width: 768px)': {
            display: 'none'
          }
        }}
      >
        NEXT
      </NextButton>

      {/* 모바일용 NextButton */}
      <MobileNextButtonWrapper>
        <NextButton 
          onClick={handleNextClick}
          style={{
            opacity: 1
          }}
        >
          NEXT
        </NextButton>
      </MobileNextButtonWrapper>
    </ResidentialPageContainer>
  );
}

// PersonalInfo1 관련 입력 칸 스타일 컴포넌트들
const PersonalInput = styled(FormInput)`
  @media (max-width: 768px) {
    width: 80%;
    max-width: 80%;
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 6px;
  }
`;

// PersonalFormContainer 스타일 수정
const PersonalFormContainer = styled(SplitFormContainer)`
  display: flex;
  gap: 20px;
  position: absolute;
  padding: 0 20px;
  right: 200px;
  width: 700px;
  top: 50%;
  transform: translateY(-50%);
  
  @media (max-width: 1440px) {
    right: 90px;
    width: 660px;
  }

  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    padding: 0 20px;
    padding-bottom: 0;
    padding-left: 60px;
    margin-top: 40px;
  }
`;

// PersonalFormColumn 스타일 수정
const PersonalFormColumn = styled(FormColumn)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 20px;
  width: 50%;
  height: auto;
  padding: 10px 0;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
    gap: 16px;

    ${FormGroup} {
      margin-bottom: 16px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const PersonalFormGroup = styled(FormGroup)`
  @media (max-width: 768px) {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const PersonalFormLabel = styled(FormLabel)`

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 4px;
    padding: 0;
  }
`;

const PersonalCheckboxLabel = styled(PrivacyCheckboxLabel)`
  @media (max-width: 768px) {
    width: 90%;
    margin-top: 8px;
    font-size: 13px;
    padding: 0;
  }
`;

// PersonalTitle 스타일 수정
const PersonalTitle = styled(ResidentialTitle)`
  position: absolute;
  left: 280px;
  top: 45%;
  transform: translateY(-50%);
  font-size: 40px;
  font-weight: 300;
  color: white;
  line-height: 1.5;
  white-space: pre-line;
  width: 30%;

  @media (max-width: 768px) {
    position: static;
    transform: none;
    margin-top: 140px;
    margin-bottom: 24px;
    padding: 0;
    font-size: 36px;
    text-align: left;
    line-height: 1.4;
    width: 100%;
  }

  @media (max-width: 375px) {
    margin-top: 120px;
    font-size: 32px;
  }
`;

// PersonalInfo1 컴포넌트 수정
function PersonalInfo1({ dispatch, state }) {
  const { handleTransition } = useContext(TransitionContext);
  const { isVideoPaused } = useContext(VideoContext);

  const handleBack = () => {
    if (state.selectedPage === 'residential') {
      handleTransition('/residential2', true);
    } else if (state.selectedPage === 'commercial') {
      handleTransition('/commercial', true);
    }
  };

  const handleNext = () => {
    // 필수 필드 검증
    if (missingFieldsAlert(state, 'personal')) {
      return;
    }
    handleTransition('/personal-info2');
  };

  // 폼 유효성 검사 함수
  const isFormValid = () => {
    const requiredFields = ['name', 'phone', 'email', 'privacyPolicyAccepted'];
    return requiredFields.every(field => state[field] && state[field].toString().trim() !== '');
  };

  return (
    <ResidentialPageContainer>
      <BackButton onClick={handleBack} />

      {isVideoPaused && (
        <>
          <PersonalTitle>
            연락드릴게요{'\n'}연락처를 알려주세요
          </PersonalTitle>

          <PersonalFormContainer>
            {/* 왼쪽 컬럼 */}
            <PersonalFormColumn>
              <PersonalFormGroup>
                <PersonalFormLabel>성함을 입력해주세요</PersonalFormLabel>
                <PersonalInput
                  type="text"
                  placeholder="성함"
                  value={state.name}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FIELD', field: 'name', value: e.target.value })
                  }
                  required
                />
              </PersonalFormGroup>

              <PersonalFormGroup>
                <PersonalFormLabel>연락처를 입력해주세요</PersonalFormLabel>
                <PersonalInput
                  type="tel"
                  placeholder="휴대전화"
                  value={state.phone || ''}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FIELD', field: 'phone', value: e.target.value })
                  }
                  required
                />
              </PersonalFormGroup>

              <PersonalFormGroup>
                <PersonalFormLabel>이메일을 입력해주세요</PersonalFormLabel>
                <PersonalInput
                  type="email"
                  placeholder="이메일"
                  value={state.email || ''}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FIELD', field: 'email', value: e.target.value })
                  }
                  required
                />
              </PersonalFormGroup>

              {/* 새로 추가되는 문의사항 입력 필드 */}
              <PersonalFormGroup>
                <PersonalFormLabel>저희가 알아야 할 내용이 있을까요?</PersonalFormLabel>
                <PersonalTextArea
                  placeholder="문의하실 내용을 자유롭게 작성해주세요"
                  value={state.inquiry || ''}
                  onChange={(e) =>
                    dispatch({ type: 'SET_FIELD', field: 'inquiry', value: e.target.value })
                  }
                />
              </PersonalFormGroup>
            </PersonalFormColumn>

            {/* 오른쪽 컬럼 */}
            <PersonalFormColumn>
              <PersonalFormGroup isPrivacyPolicy>
                <PersonalFormLabel>개인정보 처리방침</PersonalFormLabel>
                <PersonalPrivacyBox>
                  <p>비스페이스는(은) (이하 '회사'는) 고객님의 개인정보를 중요시하며, "정보통신망 이용촉진 및 정보보호"에 관한 법률을 준수하고 있습니다.
회사는 개인정보처리방침을 통하여 고객님께서 제공하시는 개인정보가 어떠한 용도와 방식으로 이용되고 있으며, 개인정보보호를 위해 어떠한 조치가 취해지고 있는지 알려드립니다.

회사는 개인정보처리방침을 개정하는 경우 웹사이트 공지사항(또는 개별공지)을 통하여 공지할 것입니다.
- 본 방침은 : 2024년 04월 29일부터 시행됩니다.

1. 수집하는 개인정보 항목
회사는 회원가입, 상담, 서비스 신청 등등을 위해 아래와 같은 개인정보를 수집하고 있습니다.

- 수집항목 : 이름, 생년월일, 성별 , 로그인 ID, 비밀번호, 비밀번호 질문과 답변, 자택 전화번호, 자택 주소, 휴대폰번호, 이메일, 쿠키, 결제기록
- 개인정보 수집방법 : 홈페이지 (회원가입)

2. 개인정보의 수집 및 이용목적
회사는 수집한 개인정보를 다음의 목적을 위해 활용합니다.

서비스 제공에 관한 계약 이행 및 서비스 제공에 따른 요금정산
콘텐츠 제공 , 구매 및 요금 결제 , 물품배송 또는 청구지 등 발송 , 금융거래 본인 인증 및 금융 서비스
회원 관리
회원제 서비스 이용에 따른 본인확인 , 개인 식별 , 불량회원의 부정 이용 방지와 비인가 사용 방지 , 가입 의사 확인 , 연령확인 , 불만처리 등 민원처리 , 고지사항 전달 마케팅 및 광고에 활용 이벤트 등 광고성 정보 전달 , 접속 빈도 파악 또는 회원의 서비스 이용에 대한 통계

3. 개인정보의 보유 및 이용기간
원칙적으로, 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기합니다. 단, 관계법령의 규정에 의하여 보존할 필요가 있는 경우 회사는 아래와 같이 관계법령에서 정한 일정한 기간 동안 회원정보를 보관합니다.

- 보존 항목 : 결제기록
- 보존 근거 : 계약 또는 청약철회 등에 관한 기록
- 보존 기간 : 3년
- 계약 또는 청약철회 등에 관한 기록 : 5년 (전자상거래등에서의 소비자보호에 관한 법률)
- 대금결제 및 재화 등의 공급에 관한 기록 : 5년 (전자상거래등에서의 소비자보호에 관한 법률)
- 소비자의 불만 또는 분쟁처리에 관한 기록 : 3년 (전자상거래등에서의 소비자보호에 관한 법률)
4. 개인정보의 파기절차 및 방법
회사는 원칙적으로 개인정보 수집 및 이용목적이 달성된 후에는 해당 정보를 지체없이 파기합니다. 파기절차 및 방법은 다음과 같습니다.

파기절차
회원님이 회원가입 등을 위해 입력하신 정보는 목적이 달성된 후 별도의 DB로 옮겨져(종이의 경우 별도의 서류함) 내부 방침 및 기타 관련 법령에 의한 정보보호 사유에 따라(보유 및 이용기간 참조) 일정 기간 저장된 후 파기되어집니다.
별도 DB로 옮겨진 개인정보는 법률에 의한 경우가 아니고서는 보유되어지는 이외의 다른 목적으로 이용되지 않습니다.
파기방법
전자적 파일형태로 저장된 개인정보는 기록을 재생할 수 없는 기술적 방법을 사용하여 삭제합니다.

5. 개인정보 제공
회사는 이용자의 개인정보를 원칙적으로 외부에 제공하지 않습니다. 다만, 아래의 경우에는 예외로 합니다.

- 이용자들이 사전에 동의한 경우
- 법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우

6. 이용자 및 법정대리인의 권리와 그 행사방법
- 이용자는 언제든지 등록되어 있는 자신의 개인정보를 조회하거나 수정할 수 있으며 가입해지를 요청할 수도 있습니다.
- 이용자들의 개인정보 조회,수정을 위해서는 '개인정보변경'(또는 '회원정보수정' 등)을 가입해지(동의철회)를 위해서는 "회원탈퇴"를 클릭하여 본인 확인 절차를 거치신 후 직접 열람, 정정 또는 탈퇴가 가능합니다.
- 혹은 개인정보관리책임자에게 서면, 전화 또는 이메일로 연락하시면 지체없이 조치하겠습니다.
- 귀하가 개인정보의 오류에 대한 정정을 요청하신 경우에는 정정을 완료하기 전까지 당해 개인정보를 이용 또는 제공하지 않습니다. 또한 잘못된 개인정보를 제3자에게 이미 제공한 경우에는 정정 처리결과를 제3자에게 지체없이 통지하여 정정이 이루어지도록 하겠습니다.
- 회사는 이용자의 요청에 의해 해지 또는 삭제된 개인정보는 "회사가 수집하는 개인정보의 보유 및 이용기간"에 명시된 바에 따라 처리하고 그 외의 용도로 열람 또는 이용할 수 없도록 처리하고 있습니다.
- 만14미만 아동의 경우 법정대리인이 아동의 개인정보를 조회하거나 수정할 권리 수집 및 이용 동의를 철회할 권리를 가집니다.

7. 개인정보 자동수집 장치의 설치, 운영 및 그 거부에 관한 사항
회사는 귀하의 정보를 수시로 저장하고 찾아내는 '쿠키(cookie)' 등을 운용합니다. 쿠키란 웹사이트를 운영하는데 이용되는 서버가 귀하의 브라우저에 보내는 아주 작은 텍스트 파일로서 귀하의 컴퓨터 하드디스크에 저장됩니다.
회사는 다음과 같은 목적을 위해 쿠키를 사용합니다.

쿠키 등 사용 목적
회원과 비회원의 접속 빈도나 방문 시간 등을 분석, 이용자의 취향과 관심분야를 파악 및 자취 추적, 각종 이벤트 참여 정도 및 방문 회수 파악 등을 통한 타겟 마케팅 및 개인 맞춤 서비스 제공
귀하는 쿠키 설치에 대한 선택권을 가지고 있습니다. 따라서, 귀하는 웹브라우저에서 옵션을 설정함으로써 모든 쿠키를 허용하거나, 쿠키가 저장될 때마다 확인을 거치거나, 아니면 모든 쿠키의 저장을 거부할 수도 있습니다.
쿠키 설정 거부 방법
쿠키 설정을 거부하는 방법으로는 회원님이 사용하시는 웹 브라우저의 옵션을 선택함으로써 모든 쿠키를 허용하거나 쿠키가 저장될 때마다 확인을 거치거나, 모든 쿠키의 저장을 거부할 수 있습니다.
설정방법 예(인터넷 익스플로어의 경우) : 웹 브라우저 상단의 도구 - 인터넷 옵션 - 개인정보
단, 귀하께서 쿠키 설치를 거부하였을 경우 서비스 제공에 어려움이 있을 수 있습니다.

8. 개인정보에 관한 민원서비스
회사는 고객의 개인정보를 보호하고 개인정보와 관련한 불만을 처리하기 위하여 아래와 같이 관련 부서 및 개인정보 보호책임자를 지정하고 있습니다.

이 름 : 김면진 주임
전 화 : 01020209511
이메일 : kmj@bspace.kr

귀하께서는 회사의 서비스를 이용하시며 발생하는 모든 개인정보보호 관련 민원을 개인정보관리책임자 혹은 담당부서로 신고하실 수 있습니다.
회사는 이용자들의 신고사항에 대해 신속하게 충분한 답변을 드릴 것입니다.
기타 개인정보침해에 대한 신고나 상담이 필요하신 경우에는 아래 기관에 문의하시기 바랍니다.

개인분쟁조정위원회 (www.1336.or.kr/1336)
정보보호마크인증위원회 (www.eprivacy.or.kr/02-580-0533~4)
대검찰청 인터넷범죄수사센터 (http://icic.sppo.go.kr/02-3480-3600)
경찰청 사이버테러대응센터 (www.ctrc.go.kr/02-392-0330)
</p>
                </PersonalPrivacyBox>
                <PersonalCheckboxLabel>
                  <Checkbox
                    type="checkbox"
                    checked={state.privacyPolicyAccepted}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_FIELD',
                        field: 'privacyPolicyAccepted',
                        value: e.target.checked,
                      })
                    }
                    required
                  />
                  개인정보 처리방침에 동의합니다.
                </PersonalCheckboxLabel>
              </PersonalFormGroup>
            </PersonalFormColumn>
          </PersonalFormContainer>

          {/* PC용 NextButton */}
          <NextButton onClick={handleNext}>
            NEXT
          </NextButton>

          {/* 모바일용 NextButton */}
          <MobileNextButtonWrapper>
            <NextButton onClick={handleNext}>
              NEXT
            </NextButton>
          </MobileNextButtonWrapper>
        </>
      )}
    </ResidentialPageContainer>
  );
}

// PersonalTextArea 스타일 컴포넌트 추가
const PersonalTextArea = styled.textarea`
  width: 80%;
  height: 60px;
  padding: 16px;
  border-radius: 8px;
  border: none;
  background: white;
  color: #FF4B4B;
  font-size: 15px;
  resize: none;
  font-family: inherit;

  &::placeholder {
    color: #FF4B4B;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 80%;
    height: 60px;
    border-radius: 6px;
    padding: 12px 16px;
    font-size: 15px;
  }

  @media (max-width: 375px) {
    height: 90px;
    font-size: 14px;
  }
`;

// FileUploadButton 컴포넌트 추가
const FileUploadButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 130px;
  height: 52px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid white;
  background: transparent;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (max-width: 768px) {
    width: 80%; // PersonalInfo1과 동일한 너비로 변경
    height: 48px;
    font-size: 15px;
    border-radius: 6px;
  }
`;

// YesNoRadioGroup 컴포넌트 추가
const YesNoRadioGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  
  @media (max-width: 768px) {
    width: 90%;
    display: flex;
    gap: 12px;
  }
`;

// YesNoButton 컴포넌트 추가
const YesNoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.selected ? 'transparent' : '#ffffff'};
  color: ${props => props.selected ? '#ffffff' : '#FF4B4B'};
  border: 2px solid white;
  padding: 0.8rem 1.6rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin: 0;
  max-width: 158px;
  border-radius: 10px;
  height: 52px;
  flex: 1;

  &:hover {
    background-color: ${props => props.selected ? 'rgba(255, 255, 255, 0.1)' : '#f8f8f8'};
  }

  @media (max-width: 768px) {
    height: 48px;
    padding: 0;
    font-size: 15px;
    border-radius: 6px;
    min-width: 0;
  }
`;

// BudgetInputWrapper 컴포넌트 추가
const BudgetInputWrapper = styled.div`
  position: relative;
  width: 290px;
  height: 48px;

  @media (max-width: 768px) {
    width: 80%;
    
    input {
      width: 87%;
      padding-right: 48px;
    }
  }
`;

// BudgetInput 컴포넌트 추가
const BudgetInput = styled.input`
  width: 88%;
  padding: 0 48px 0 16px;
  border: 1px solid white;
  border-radius: 8px;
  background: white;
  color: #FF4B4B;
  font-size: 1rem;
  height: 52px;

  &::placeholder {
    color: #FF4B4B;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    height: 48px;
    font-size: 15px;
    border-radius: 6px;
  }
`;

// BudgetUnitText 컴포넌트 추가
const BudgetUnitText = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #FF4B4B;
  font-size: 15px;
  pointer-events: none;
`;

// FormInput 아래에 추가
const PrioritySelect = styled(FormSelect)`
  @media (max-width: 768px) {
    width: 90%;
    max-width: 90%;
    height: 48px;
    padding: 0 16px;
    font-size: 15px;
    border-radius: 6px;
    background-position: right 16px center;
    
    // 드롭다운 화살표 아이콘 위치 조정
    background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%23FF4B4B' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    
    // 옵션 텍스트 스타일
    option {
      font-size: 15px;
      padding: 8px;
    }
  }
`;

// 새로운 CenteredNextButton 컴포넌트 추가
const CenteredNextButton = styled(NextButton)`
  @media (max-width: 768px) {
    position: fixed;
    left: 20px;  // 좌측 여백 20px로 설정
    right: 20px; // 우측 여백도 20px로 설정
    width: calc(100% - 40px); // 전체 너비에서 좌우 여백 40px 제외
    transform: none;
    min-width: unset; // 기존 min-width 제거
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0; // 패딩 초기화
  }

  @media (max-width: 375px) {
    left: 20px;
    right: 20px;
    width: calc(100% - 40px);
  }
`;

// PersonalInfo2RadioGroup 컴포넌트 추가
const PersonalInfo2RadioGroup = styled.div`
  display: flex;
  gap: 1rem;
  transform: translateX(200px);
  
  & + & {
    margin-top: 20px;
  }

  @media (max-width: 768px) {
    transform: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
    padding: 0px;
    margin-top: 0;
    align-items: center;
    justify-items: center;
    
    & + & {
      margin-top: 12px;
    }

    // RadioButton 컨테이너 스타일 조정
    > button {
      width: 100%; // 버튼 너비를 그리드 셀에 맞춤
      justify-content: center; // 버튼 내 콘텐츠 중앙 정렬
    }
  }

  @media (max-width: 375px) {
    transform: none;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    width: 100%;
    padding: 0px;
    margin-top: 0;
    
    & + & {
      margin-top: 8px;
    }
  }
`;

// FormContainer 스타일 컴포넌트 추가 (GlobalStyle ��래에 추가)
const FormContainer = styled.div`
  display: flex;
  gap: 20px;
  position: absolute;
  right: 200px;
  width: 700px;
  top: 50%;
  transform: translateY(-50%);
  
  @media (max-width: 1440px) {
    right: 90px;
    width: 660px;
  }
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    width: 100%;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    margin-top: 20px;
    padding-bottom: 120px;
  }

  @media (max-width: 375px) {
    padding: 0;
    margin-top: 16px;
  }
`;

// MobileNextButtonWrapper 스타일 컴포넌트 추가
const MobileNextButtonWrapper = styled.div`
  display: none; // PC에서는 숨김

  @media (max-width: 768px) {
    display: flex;
    justify-content: flex-end;
    padding: 20px;
    width: 100%;
    position: relative;
    z-index: 1;

    ${NextButton} {
      display: flex; // 모바일에서는 다시 보이게 함
      position: static;
      width: 120px;
      height: 44px;
      min-width: 120px;
      font-size: 15px;
      padding: 0;
      margin-left: auto;
      margin-top: 40px;
      margin-bottom: calc(60px + env(safe-area-inset-bottom)); // iOS safe area 고려
      transform: none !important;
      transition: opacity 0.2s ease-out;
      animation: none;
      will-change: opacity;
      justify-content: center;
      align-items: center;

      &::after {
        font-size: 13px;
        margin-left: 6px;
      }
    }
  }

  @media (max-width: 375px) {
    ${NextButton} {
      height: 40px;
      font-size: 14px;
    }
  }
`;

// 새로운 스타일 컴포넌트 추가
const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 85%;
    padding: 20px;
  }
`;

const PopupTitle = styled.h3`
  color: #FF4B4B;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledCheckbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: #FF4B4B;
  font-size: 15px;
  cursor: pointer;
`;

const PopupButton = styled.button`
  background-color: #FF4B4B;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #ff3333;
  }
`;

const PriorityInput = styled(FormInput)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 290px;
  height: 52px !important;
  resize: none;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;

  @media (max-width: 768px) {
    width: 80%;
    height: 48px !important;
  }
`;

const PriorityTag = styled.span`
  background: #FF4B4B;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

// PrioritySelector 컴포넌트 수정
function PrioritySelector({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const priorities = [
    "합리적인 가격",
    "디자인&마감 퀄리티",
    "공간에 대한 이해도",
    "현장 경험치",
    "업무 소통 능력",
    "리더십",
    "사후 관리"
  ];

  const selectedPriorities = value ? value.split(',').filter(Boolean) : [];

  const handleToggle = (priority) => {
    let newPriorities;
    if (selectedPriorities.includes(priority)) {
      newPriorities = selectedPriorities.filter(p => p !== priority);
    } else {
      newPriorities = [...selectedPriorities, priority];
    }
    onChange(newPriorities.join(','));
  };

  return (
    <>
      <PriorityInput
        as="input"
        readOnly
        placeholder="선택하세요"
        value={selectedPriorities.join(', ')}
        onClick={() => setIsOpen(true)}
        style={{ 
          padding: '0px 16px',
          lineHeight: '1.4'
        }}
      />

      {isOpen && (
        <PopupOverlay onClick={() => setIsOpen(false)}>
          <PopupContent onClick={e => e.stopPropagation()}>
            <PopupTitle>중요하게 생각하는 항목을 선택해주세요</PopupTitle>
            <CheckboxContainer>
              {priorities.map(priority => (
                <CheckboxItem key={priority}>
                  <StyledCheckbox
                    type="checkbox"
                    id={priority}
                    checked={selectedPriorities.includes(priority)}
                    onChange={() => handleToggle(priority)}
                  />
                  <CheckboxLabel htmlFor={priority}>{priority}</CheckboxLabel>
                </CheckboxItem>
              ))}
            </CheckboxContainer>
            <PopupButton onClick={() => setIsOpen(false)}>
              선택 완료
            </PopupButton>
          </PopupContent>
        </PopupOverlay>
      )}
    </>
  );
}

// PortfolioButton을 ExploreButton으로 대체
const ExploreButton = styled(motion.button)`
  background-color: white;
  border: 1px solid white;
  color: #FF4B4B;
  padding: 15px 40px;
  font-size: 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  font-weight: 500;
  text-decoration: none;
  min-width: 180px; // 텍스트가 길어져서 너비 증가

  &::after {
    content: '▶';
    margin-left: 8px;
    font-size: 15px;
    transition: all 0.3s ease;
  }

  &:hover {
    background-color: transparent;
    color: white;
    
    &::after {
      color: white;
    }
  }

  @media (max-width: 768px) {
    width: 300px; // 모바일에서도 일관된 너비 유지
    height: 48px;
    justify-content: center;
    font-size: 18px;
    padding: 0;
  }
`;

// 새로운 스타일 컴포넌트 추가
const DirectCallButton = styled(ExploreButton)`
  display: none; // PC에서는 숨김

  @media (max-width: 768px) {
    display: flex; // 모바일에서만 표시
    width: 300px;
    height: 48px;
    justify-content: center;
    font-size: 18px;
    padding: 0;
  }
`;

